import { useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { GlobeSlide } from './slides/GlobeSlide';
import { ImportSlide } from './slides/ImportSlide';
import { CrewSlide } from './slides/CrewSlide';
import { CTASlide } from './slides/CTASlide';
import { track } from '../../analytics';

const ONBOARDING_KEY = 'rostfound_onboarding_seen';
const SLIDE_COUNT = 4;

export function hasSeenOnboarding(): boolean {
  return localStorage.getItem(ONBOARDING_KEY) === '1';
}

function markOnboardingSeen(): void {
  localStorage.setItem(ONBOARDING_KEY, '1');
}

export function resetOnboarding(): void {
  localStorage.removeItem(ONBOARDING_KEY);
}

// ── Component ──

interface OnboardingWalkthroughProps {
  onComplete: (action?: 'import-roster') => void;
}

export function OnboardingWalkthrough({ onComplete }: OnboardingWalkthroughProps) {
  const [slide, setSlide] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right'>('left');
  const [animating, setAnimating] = useState(false);
  const touchRef = useRef<{ startX: number; startY: number } | null>(null);

  const finish = useCallback(
    (action?: 'import-roster') => {
      markOnboardingSeen();
      track('onboarding_completed', {
        action: action === 'import-roster' ? 'import' : 'explore',
      });
      onComplete(action);
    },
    [onComplete],
  );

  const skip = useCallback(() => {
    markOnboardingSeen();
    track('onboarding_completed', { action: 'skip', slide });
    onComplete();
  }, [onComplete, slide]);

  const goTo = useCallback(
    (next: number) => {
      if (animating || next === slide || next < 0 || next >= SLIDE_COUNT) return;
      setDirection(next > slide ? 'left' : 'right');
      setAnimating(true);
      // Small delay so direction class applies before transition
      requestAnimationFrame(() => {
        setSlide(next);
        setTimeout(() => setAnimating(false), 400);
      });
    },
    [slide, animating],
  );

  // Touch handlers for swipe
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY };
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchRef.current) return;
      const dx = e.changedTouches[0].clientX - touchRef.current.startX;
      const dy = e.changedTouches[0].clientY - touchRef.current.startY;
      touchRef.current = null;
      // Only swipe if mostly horizontal and > 50px
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (dx < 0 && slide < SLIDE_COUNT - 1) goTo(slide + 1);
        else if (dx > 0 && slide > 0) goTo(slide - 1);
      }
    },
    [slide, goTo],
  );

  const portalTarget = document.getElementById('app-shell') || document.body;

  return createPortal(
    <div
      className="absolute inset-0 z-[200] bg-slate-900 flex flex-col overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <OnboardingAnimations />

      {/* Skip button */}
      {slide < SLIDE_COUNT - 1 && (
        <button
          onClick={skip}
          className="absolute top-[calc(var(--sat,0px)+12px)] right-4 text-sm text-slate-500 hover:text-slate-300 transition-colors py-2 px-3 z-10"
        >
          Skip
        </button>
      )}

      {/* Slide content */}
      <div className="flex-1 flex relative">
        <SlideWrapper active={slide === 0} direction={direction}>
          <GlobeSlide active={slide === 0} />
        </SlideWrapper>
        <SlideWrapper active={slide === 1} direction={direction}>
          <ImportSlide active={slide === 1} />
        </SlideWrapper>
        <SlideWrapper active={slide === 2} direction={direction}>
          <CrewSlide active={slide === 2} />
        </SlideWrapper>
        <SlideWrapper active={slide === 3} direction={direction}>
          <CTASlide
            active={slide === 3}
            onImport={() => finish('import-roster')}
            onExplore={() => finish()}
          />
        </SlideWrapper>
      </div>

      {/* Dot indicators + nav */}
      {slide < SLIDE_COUNT - 1 && (
        <div className="px-6 pb-[calc(var(--sab,0px)+20px)] flex items-center justify-between">
          {/* Dots */}
          <div className="flex gap-2">
            {Array.from({ length: SLIDE_COUNT }, (_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === slide
                    ? 'w-6 bg-accent'
                    : 'w-2 bg-slate-600 hover:bg-slate-500'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          {/* Next button */}
          <button
            onClick={() => goTo(slide + 1)}
            className="px-6 py-2.5 rounded-xl bg-accent/10 border border-accent/20 text-accent font-semibold text-sm active:scale-[0.96] transition-all"
          >
            Next
          </button>
        </div>
      )}
    </div>,
    portalTarget,
  );
}

// ── Slide wrapper with enter/exit transitions ──

function SlideWrapper({
  active,
  direction,
  children,
}: {
  active: boolean;
  direction: 'left' | 'right';
  children: React.ReactNode;
}) {
  // Inactive slides are hidden but remain mounted so animations don't re-trigger
  const translateX = active ? '0%' : direction === 'left' ? '100%' : '-100%';

  return (
    <div
      className="absolute inset-0 flex flex-col transition-all duration-400 ease-[cubic-bezier(0.32,0.72,0,1)]"
      style={{
        transform: `translateX(${translateX})`,
        opacity: active ? 1 : 0,
        pointerEvents: active ? 'auto' : 'none',
      }}
    >
      {children}
    </div>
  );
}

// ── Shared keyframe animations ──

function OnboardingAnimations() {
  return (
    <style>{`
      @keyframes ob-fade-in {
        from { opacity: 0; transform: scale(0.96); }
        to { opacity: 1; transform: scale(1); }
      }
      @keyframes ob-slide-up {
        from { transform: translateY(24px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      @keyframes ob-hero-pop {
        0% { transform: scale(0.4) rotate(-15deg); opacity: 0; }
        60% { transform: scale(1.08) rotate(3deg); opacity: 1; }
        100% { transform: scale(1) rotate(0deg); opacity: 1; }
      }
      @keyframes ob-glow-pulse {
        0%, 100% { box-shadow: 0 0 30px rgba(140,106,46,0.1), 0 0 60px rgba(140,106,46,0.05); }
        50% { box-shadow: 0 0 40px rgba(140,106,46,0.2), 0 0 80px rgba(140,106,46,0.08); }
      }
      @keyframes ob-flash {
        0% { opacity: 0.9; }
        100% { opacity: 0; }
      }
      @keyframes ob-phone-fade {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes ob-scan {
        0% { top: 0; }
        50% { top: calc(100% - 2px); }
        100% { top: 0; }
      }
    `}</style>
  );
}
